/** Shared isolation and grouping policy for layered design exports. See ../psd-export/AGENTS.md. */
import { layerScale } from "@hightouch/lib/ad-studio/html-editor/layer-css";
import type { BlendMode } from "ag-psd";
import {
  type Doc,
  type GroupLayer,
  type Layer,
  type TextLayer,
  isFlexGroup,
} from "@hightouch/lib/ad-studio/html-editor/model";
import type { TextSegment } from "@hightouch/lib/ad-studio/html-editor/text-segments";
import {
  type Affine,
  boxTransform,
  IDENTITY,
  multiply,
} from "@hightouch/lib/ad-studio/psd-export/affine";
import { parseCssLength } from "@hightouch/lib/ad-studio/psd-export/css-length";
import { resolveTransformOrigin } from "@hightouch/lib/ad-studio/psd-export/transform-origin";
import {
  plainTextOfSegments,
  textSegmentsOf,
} from "@hightouch/lib/ad-studio/psd-export/text-layer-data";

type LayerPlanBase = {
  name: string;
  hidden: boolean;
  opacity: number;
  blendMode?: LayerBlendMode;
};
export type LayerPlan = LayerPlanBase &
  (
    | { kind: "group"; children: LayerPlan[] }
    | {
        kind: "leaf";
        captureDoc: Doc;
        editableText?: {
          layer: TextLayer;
          parentTransform: Affine;
          ancestors: GroupLayer[];
        };
      }
  );
const BACKGROUND_LAYER_NAME = "Background";

const NAME_TRUNCATE = 30;

/**
 * Keys whose rendering can't be decomposed per child, so the group ships as one flattened leaf.
 * Matched as `key` or `key-*`.
 */
const GROUP_FLATTEN_STYLE_KEYS = ["filter"];

/**
 * Keys that sample what is painted BENEATH the layer — a `backdrop-filter` blur has nothing to blur
 * in an isolated capture, so these captures keep the real stack below. Matched as `key` or `key-*`.
 */
const BACKDROP_DEPENDENT_STYLE_KEYS = ["backdrop-filter"];

const MIX_BLEND_MODE_KEY = "mix-blend-mode";

/** Exported as an unblended bitmap plus the mode; values with no counterpart flatten instead. */
const PSD_BLEND_MODE_BY_CSS = {
  normal: "normal",
  multiply: "multiply",
  screen: "screen",
  overlay: "overlay",
  darken: "darken",
  lighten: "lighten",
  "color-dodge": "color dodge",
  "color-burn": "color burn",
  "hard-light": "hard light",
  "soft-light": "soft light",
  difference: "difference",
  exclusion: "exclusion",
  hue: "hue",
  saturation: "saturation",
  color: "color",
  luminosity: "luminosity",
} as const satisfies Record<string, BlendMode>;
type LayerBlendMode =
  (typeof PSD_BLEND_MODE_BY_CSS)[keyof typeof PSD_BLEND_MODE_BY_CSS];

/** Paints the group's box: stripped from ancestor chains, kept on its own box leaf. */
const GROUP_PAINT_STYLE_KEYS = ["background", "box-shadow", "outline"];

// ── extraStyles predicates ──────────────────────────────────────────────────

/** A predicate for one style-key family: the key itself, or any `key-*` longhand of it. */
function styleKeyIn(candidates: string[]): (key: string) => boolean {
  return (key) =>
    candidates.some(
      (candidate) => key === candidate || key.startsWith(`${candidate}-`),
    );
}

const isBackdropDependentKey = styleKeyIn(BACKDROP_DEPENDENT_STYLE_KEYS);
const isGroupFlattenKey = styleKeyIn(GROUP_FLATTEN_STYLE_KEYS);
/** Border keys that paint; `border-radius` clips instead, so it stays. */
const isBorderPaintKey = (key: string) =>
  styleKeyIn(["border"])(key) && !styleKeyIn(["border-radius"])(key);
const isGroupPaintKey = (key: string) =>
  styleKeyIn(GROUP_PAINT_STYLE_KEYS)(key) || isBorderPaintKey(key);

/** Whether the layer declares any style key the predicate accepts. */
function hasStyleKey(layer: Layer, matches: (key: string) => boolean): boolean {
  return Object.keys(layer.extraStyles ?? {}).some(matches);
}

/** The layer with every style key `keep` rejects dropped. */
function withoutStyles<T extends Layer>(
  layer: T,
  keep: (key: string) => boolean,
): T {
  if (!layer.extraStyles) return layer;
  const kept: Record<string, string> = {};
  for (const [key, value] of Object.entries(layer.extraStyles)) {
    if (keep(key)) kept[key] = value;
  }
  return { ...layer, extraStyles: kept };
}

/** `Object.hasOwn` because the CSS value is authored markup — `constructor` must not resolve. */
function psdBlendModeOf(layer: Layer): LayerBlendMode | undefined {
  const css = layer.extraStyles?.[MIX_BLEND_MODE_KEY]?.trim().toLowerCase();
  if (!css) return undefined;
  return Object.hasOwn(PSD_BLEND_MODE_BY_CSS, css)
    ? PSD_BLEND_MODE_BY_CSS[css as keyof typeof PSD_BLEND_MODE_BY_CSS]
    : undefined;
}

/** Drops a `mix-blend-mode` shipped as a PSD blend mode, so the capture is unblended. */
function withoutRepresentedBlendMode<T extends Layer>(layer: T): T {
  if (!psdBlendModeOf(layer)) return layer;
  return withoutStyles(layer, (key) => key !== MIX_BLEND_MODE_KEY);
}

/** `Doc.background` becomes a bottom "Background" leaf. */
export function planLayers(doc: Doc): LayerPlan[] {
  const plans = doc.layers.map((layer) => planLayer(doc, layer, []));
  if (doc.background !== null) {
    plans.unshift({
      kind: "leaf",
      name: BACKGROUND_LAYER_NAME,
      hidden: false,
      opacity: 1,
      captureDoc: {
        width: doc.width,
        height: doc.height,
        background: doc.background,
        layers: [],
      },
    });
  }
  return plans;
}

function planLayer(doc: Doc, layer: Layer, ancestors: GroupLayer[]): LayerPlan {
  const blendMode = psdBlendModeOf(layer);
  // The visible text labels both the Photoshop layer and the InDesign frame.
  const segments = layer.type === "text" ? textSegmentsOf(layer) : undefined;
  const base = {
    name: psdLayerName(layer, segments),
    hidden: layer.hidden,
    opacity: layer.opacity,
    ...(blendMode ? { blendMode } : {}),
  };

  // The browser bakes everything against the real backdrop, opacity and blend mode included, so
  // none of it may be applied again on the way out. A baked backdrop also rules out type data.
  if (needsBackdropCapture(layer)) {
    return {
      kind: "leaf",
      name: base.name,
      hidden: base.hidden,
      opacity: 1,
      captureDoc: backdropStackDoc(doc, ancestors, layer),
    };
  }

  if (layer.type === "group" && !mustFlattenWholeGroup(layer)) {
    const children = layer.children.map((child) =>
      planLayer(doc, child, [...ancestors, layer]),
    );
    // Ancestor paint is stripped from descendants' captures, so it ships as this bottom leaf.
    if (groupPaintsOwnBox(layer)) {
      children.unshift({
        kind: "leaf",
        name: BACKGROUND_LAYER_NAME,
        hidden: false,
        opacity: 1,
        captureDoc: isolationDoc(doc, ancestors, { ...layer, children: [] }),
      });
    }
    return { kind: "group", ...base, children };
  }

  const plan: LayerPlan = {
    kind: "leaf",
    ...base,
    captureDoc: isolationDoc(doc, ancestors, layer),
  };
  if (layer.type === "text") {
    plan.editableText = {
      layer,
      parentTransform: ancestorTransform(ancestors),
      ancestors,
    };
  }
  return plan;
}

/**
 * Whether this layer is the one to bake over the real stack beneath it — the shallowest level where
 * nothing the PSD applies afterwards lands on those pixels twice. See AGENTS.md, backdrop policy.
 */
function needsBackdropCapture(layer: Layer): boolean {
  if (dependsOnBackdrop(layer)) return true;
  if (layer.type !== "group") return false;
  if (!layer.children.some(subtreeDependsOnBackdrop)) return false;
  return mustFlattenWholeGroup(layer) || reappliesPaint(layer);
}

function subtreeDependsOnBackdrop(layer: Layer): boolean {
  return (
    dependsOnBackdrop(layer) ||
    (layer.type === "group" && layer.children.some(subtreeDependsOnBackdrop))
  );
}

/** Whether the PSD group carries paint of its own, which would land on baked backdrop pixels. */
function reappliesPaint(group: GroupLayer): boolean {
  return group.opacity < 1 || psdBlendModeOf(group) !== undefined;
}

/** Whether the layer's own pixels depend on what is painted beneath it. */
function dependsOnBackdrop(layer: Layer): boolean {
  if (hasStyleKey(layer, isBackdropDependentKey)) return true;
  // A blend mode with no PSD counterpart is baked, and blending needs a backdrop; one with a
  // counterpart ships unblended for Photoshop to blend.
  return (
    layer.extraStyles?.[MIX_BLEND_MODE_KEY] !== undefined &&
    !psdBlendModeOf(layer)
  );
}

function mustFlattenWholeGroup(group: GroupLayer): boolean {
  // Read before the `isFlexGroup` guard, whose false branch narrows GroupLayer to `never`.
  const flattens = hasStyleKey(group, isGroupFlattenKey);
  // Flex lays children out by flow, so per-child x/y is meaningless.
  return isFlexGroup(group) || flattens;
}

function groupPaintsOwnBox(group: GroupLayer): boolean {
  return hasStyleKey(group, isGroupPaintKey);
}

/** Paint neutralized, layout box untouched: the border keeps its width, only its color drops out. */
function withoutGroupPaint(group: GroupLayer): GroupLayer {
  if (!group.extraStyles) return group;
  // Borders survive the strip: they size the box the children are placed inside.
  const stripped = withoutStyles(
    group,
    (key) => !styleKeyIn(GROUP_PAINT_STYLE_KEYS)(key),
  );
  if (!hasStyleKey(stripped, isBorderPaintKey)) return stripped;
  const extraStyles = { ...stripped.extraStyles };
  // Re-inserted last so it beats a `border` shorthand.
  delete extraStyles["border-color"];
  extraStyles["border-color"] = "transparent";
  return { ...stripped, extraStyles };
}

/**
 * Only the ancestor chain down to `leaf`, at full canvas size. Ancestors keep their placement;
 * anything the PSD layer carries itself is neutralized so it isn't applied twice (see AGENTS.md).
 */
function isolationDoc(doc: Doc, ancestors: GroupLayer[], leaf: Layer): Doc {
  let chain: Layer = withoutRepresentedBlendMode({
    ...leaf,
    opacity: 1,
    hidden: false,
  });
  for (const ancestor of [...ancestors].reverse()) {
    chain = {
      ...withoutRepresentedBlendMode(withoutGroupPaint(ancestor)),
      opacity: 1,
      hidden: false,
      children: [chain],
    };
  }
  return {
    width: doc.width,
    height: doc.height,
    background: null,
    layers: [chain],
  };
}

/**
 * The document painted up to and including `target`: the layers beneath are the backdrop it blends
 * with, everything above is dropped. Ancestors keep their paint and opacity — that paint is part of
 * the backdrop. Costs the covered layers their independent editability; see AGENTS.md.
 */
function backdropStackDoc(
  doc: Doc,
  ancestors: GroupLayer[],
  target: Layer,
): Doc {
  return {
    width: doc.width,
    height: doc.height,
    background: doc.background,
    layers: truncateAfter(doc.layers, [...ancestors, target]),
  };
}

/**
 * `path` walks from `layers` down to the target; each step drops the siblings painted after it and
 * reveals the one on the path. Visibility rides on the PSD layer, so a hidden target still has to
 * paint here or its capture would be the bare backdrop — revealed in Photoshop, a second copy of
 * the stack beneath it with no blur or blend. Siblings keep their own `hidden`: they are backdrop.
 */
function truncateAfter(layers: Layer[], path: Layer[]): Layer[] {
  const [head, ...rest] = path;
  const index = head ? layers.indexOf(head) : -1;
  // Failing open would keep the layers painted ABOVE the target in its backdrop capture.
  if (!head || index < 0) {
    throw new Error("truncateAfter: the target is not on this layer list");
  }
  const branch: Layer =
    rest.length > 0 && head.type === "group"
      ? { ...head, hidden: false, children: truncateAfter(head.children, rest) }
      : { ...head, hidden: false };
  return [...layers.slice(0, index), branch];
}

/** Composed ancestor placement, so editing type inside a rotated group doesn't snap it upright. */
function ancestorTransform(ancestors: GroupLayer[]): Affine {
  let matrix = IDENTITY;
  for (const ancestor of ancestors) {
    matrix = multiply(
      matrix,
      boxTransform({
        left: ancestor.x,
        top: ancestor.y,
        width: ancestor.width,
        height: ancestor.height,
        rotation: ancestor.rotation,
        scale: layerScale(ancestor),
        flipX: ancestor.flipX,
        flipY: ancestor.flipY,
        // No `fontSize`: a group has none, so an `em` pivot degrades to the box center.
        origin: resolveTransformOrigin({
          extraStyles: ancestor.extraStyles,
          width: ancestor.width,
          height: ancestor.height,
        }),
      }),
    );
    // A descendant's containing block is the ancestor's PADDING box, so the ancestor's borders
    // shift it (its padding does not). Composed inside the box transform: the offset is in the
    // ancestor's own space, so a rotated group carries it round. The capture renders this already.
    const { left, top } = borderOriginOffset(ancestor.extraStyles);
    if (left !== 0 || top !== 0) {
      matrix = multiply(matrix, [1, 0, 0, 1, left, top]);
    }
  }
  return matrix;
}

/** Top/left border widths of a group's box, in the declaration order CSS would cascade them. */
function borderOriginOffset(extraStyles: Record<string, string> | undefined): {
  left: number;
  top: number;
} {
  let left = 0;
  let top = 0;
  for (const [key, value] of Object.entries(extraStyles ?? {})) {
    switch (key) {
      // `border: none` and `border-style: none` are the reason a shorthand with no length reads
      // as 0 rather than CSS's `medium`: with no style there is nothing to paint.
      case "border":
        left = shorthandBorderWidth(value);
        top = left;
        break;
      case "border-width": {
        const sides = boxSideWidths(value);
        top = sides.top;
        left = sides.left;
        break;
      }
      case "border-top":
        top = shorthandBorderWidth(value);
        break;
      case "border-left":
        left = shorthandBorderWidth(value);
        break;
      case "border-top-width":
        top = parseCssLength(value) ?? top;
        break;
      case "border-left-width":
        left = parseCssLength(value) ?? left;
        break;
      default:
        break;
    }
  }
  return { left, top };
}

/** First length in a `border`/`border-top` shorthand; 0 when it declares none. */
function shorthandBorderWidth(value: string): number {
  for (const token of value.trim().split(/\s+/)) {
    const length = parseCssLength(token);
    if (length !== null) return length;
  }
  return 0;
}

/** `border-width`'s 1–4 value box shorthand, of which only top and left matter here. */
function boxSideWidths(value: string): { top: number; left: number } {
  const parts = value
    .trim()
    .split(/\s+/)
    .map((token) => parseCssLength(token) ?? 0);
  const top = parts[0] ?? 0;
  return { top, left: parts[3] ?? parts[1] ?? top };
}

function psdLayerName(
  layer: Layer,
  segments: TextSegment[] | undefined,
): string {
  if (layer.label) return layer.label;
  if (segments) {
    // Breaks become spaces, as Photoshop names type layers. Via the shared lowering, since a
    // tag-stripping regex would fuse the lines into one.
    const text = plainTextOfSegments(segments).replace(/\s+/g, " ").trim();
    if (text) {
      return text.length > NAME_TRUNCATE
        ? `${text.slice(0, NAME_TRUNCATE)}…`
        : text;
    }
  }
  switch (layer.type) {
    case "rect":
      return "Rectangle";
    case "image":
      return "Image";
    case "svg":
      return "Graphic";
    case "group":
      return "Group";
    default:
      return "Layer";
  }
}
